// AiAssistant.tsx
import React, { useState, useRef, useEffect } from 'react';

interface Message {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  timestamp: string;
  document?: {
    titlu: string;
    link: string;
  };
  exemplo?: string;
}

const AiAssistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      sender: 'ai',
      text: 'Bună ziua! Sunt asistentul virtual al Patronatului Vrancea. Cum vă pot ajuta astăzi?',
      timestamp: new Date().toLocaleTimeString(),
    }
  ]);
  
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Auto-scroll la mesaje noi
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Focus pe input la încărcare
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Răspunsuri simulate
  const simulatedResponses: { [key: string]: Message } = {
    'fonduri': {
      id: '0',
      sender: 'ai',
      text: 'Există mai multe programe de finanțare disponibile pentru antreprenorii din județul Vrancea. Pentru IMM-uri, recomand în special programul „Start-Up Nation 2025" și „Granturi pentru digitalizare".',
      timestamp: '',
      document: {
        titlu: 'Ghid pentru obținerea fondurilor europene 2025',
        link: '#ghid-fonduri-2025'
      },
      exemplo: 'Din documentația existentă: Programul Start-Up Nation 2025 oferă până la 200.000 RON nerambursabili pentru întreprinderile nou înființate care angajează minim 2 persoane.'
    },
    'taxe': {
      id: '0',
      sender: 'ai',
      text: 'Pentru informații despre taxele și impozitele locale aplicabile în 2025, vă recomand să consultați documentul atașat. Principalele modificări din acest an includ reducerea impozitului pe profit pentru IMM-uri de la 16% la 10% și noile facilități fiscale pentru companiile din sectorul IT.',
      timestamp: '',
      document: {
        titlu: 'Legislație fiscală 2025 - modificări importante',
        link: '#legislatie-fiscala-2025'
      },
      exemplo: 'Din documentul legislativ: „Începând cu 1 ianuarie 2025, microîntreprinderile cu cifra de afaceri anuală sub 500.000 euro beneficiază de o cotă redusă de impozitare de 1% din venituri."'
    },
    'marketing': {
      id: '0',
      sender: 'ai',
      text: 'Pentru strategii de marketing eficiente pentru afacerile locale, recomand să vă concentrați pe prezența digitală, parteneriate locale și utilizarea rețelelor sociale pentru promovare. Puteți găsi mai multe detalii în documentul atașat.',
      timestamp: '',
      document: {
        titlu: 'Strategie de marketing pentru IMM-uri',
        link: '#strategie-marketing-imm'
      },
      exemplo: 'Din ghidul de marketing: „Companiile locale pot crește vizibilitatea cu până la 40% prin implementarea unei strategii combinate de SEO local și campanii targetate pe Facebook și Instagram."'
    },
    'default': {
      id: '0',
      sender: 'ai',
      text: 'Vă mulțumesc pentru întrebare. Am identificat câteva resurse care v-ar putea ajuta. Vă recomand să consultați documentele disponibile în biblioteca Patronatului Vrancea pentru informații mai detaliate.',
      timestamp: '',
      document: {
        titlu: 'Biblioteca de documente a Patronatului',
        link: '#biblioteca'
      },
      exemplo: 'În documentele noastre veți găsi informații despre finanțări, strategii de dezvoltare, legislație relevantă și multe alte resurse utile pentru antreprenori.'
    }
  };

  // Funcție pentru trimiterea mesajului
  const handleSendMessage = () => {
    if (inputMessage.trim() === '') return;
    
    // Adaugă mesajul utilizatorului
    const userMessage: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: inputMessage,
      timestamp: new Date().toLocaleTimeString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    
    // Simulează răspunsul AI
    setIsTyping(true);
    
    // Determină ce tip de răspuns să returneze bazat pe cuvinte cheie
    setTimeout(() => {
      let responseMessage: Message;
      
      if (inputMessage.toLowerCase().includes('fond') || inputMessage.toLowerCase().includes('finanț')) {
        responseMessage = { ...simulatedResponses['fonduri'], id: Date.now().toString(), timestamp: new Date().toLocaleTimeString() };
      } else if (inputMessage.toLowerCase().includes('tax') || inputMessage.toLowerCase().includes('impozit') || inputMessage.toLowerCase().includes('fiscal')) {
        responseMessage = { ...simulatedResponses['taxe'], id: Date.now().toString(), timestamp: new Date().toLocaleTimeString() };
      } else if (inputMessage.toLowerCase().includes('marketing') || inputMessage.toLowerCase().includes('promovare')) {
        responseMessage = { ...simulatedResponses['marketing'], id: Date.now().toString(), timestamp: new Date().toLocaleTimeString() };
      } else {
        responseMessage = { ...simulatedResponses['default'], id: Date.now().toString(), timestamp: new Date().toLocaleTimeString() };
      }
      
      setMessages(prev => [...prev, responseMessage]);
      setIsTyping(false);
    }, 1500);
  };

  // Gestionează trimiterea la apăsarea Enter (fără Shift)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Ajustează automat înălțimea input-ului pentru a se potrivi conținutului
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    
    // Resetează înălțimea
    e.target.style.height = 'auto';
    
    // Setează înălțimea bazată pe conținut, cu maxim 150px
    const newHeight = Math.min(e.target.scrollHeight, 150);
    e.target.style.height = `${newHeight}px`;
  };

  return (
    <div className="bg-white rounded-lg shadow flex flex-col h-[600px]">
      {/* Header */}
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold">Asistent AI</h2>
        <p className="text-gray-600">Întreabă orice legat de afacerea ta și primește răspunsuri bazate pe documentele comunității</p>
      </div>

      {/* Zona de mesaje */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[80%] rounded-lg p-3 ${
                  message.sender === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {/* Conținut mesaj */}
                <div className="text-sm md:text-base whitespace-pre-wrap">
                  {message.text}
                </div>
                
                {/* Extras din document (doar pentru AI) */}
                {message.sender === 'ai' && message.exemplo && (
                  <div className="mt-3 pt-3 border-t border-gray-300 text-sm">
                    <div className="font-semibold mb-1">Extras relevant:</div>
                    <div className="italic">{message.exemplo}</div>
                  </div>
                )}
                
                {/* Link către document (doar pentru AI) */}
                {message.sender === 'ai' && message.document && (
                  <div className="mt-3 pt-3 border-t border-gray-300">
                    <a 
                      href={message.document.link} 
                      className="text-sm flex items-center text-blue-700 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="mr-1">📄</span>
                      Descarcă: {message.document.titlu}
                    </a>
                  </div>
                )}
                
                {/* Timestamp */}
                <div className={`text-xs mt-1 ${
                  message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {message.timestamp}
                </div>
              </div>
            </div>
          ))}
          
          {/* Indicator "se scrie" */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg p-3 max-w-[80%]">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"></div>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          {/* Referință pentru auto-scroll */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Placeholder pentru informație */}
      <div className="px-4 py-2 bg-blue-50 text-blue-700 text-xs text-center border-t border-blue-100">
        AI răspunde în funcție de documentele încărcate de membrii comunității.
      </div>

      {/* Zona de input */}
      <div className="p-4 border-t">
        <div className="flex">
          <textarea
            ref={inputRef}
            className="flex-1 p-2 border rounded-l-md resize-none min-h-[40px]"
            placeholder="Scrie un mesaj..."
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-r-md"
            onClick={handleSendMessage}
          >
            <span className="hidden sm:inline mr-1">Trimite</span>
            <span>📤</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiAssistant;